import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getIdToken } from "src/domains/shared/utils/amplifyCompat";
import notice from "src/domains/shared/utils/notice";

/**
 * FlightHub 2 진입 엔드포인트 — 서버가 Cognito 토큰 검증·허용 도메인 인가 후
 * SSO discovery 를 대행하고 `redirectUrl` 을 반환한다.
 */
const FLIGHTHUB_ENTRY_ENDPOINT = "/api/flighthub/entry";

interface FlightHubEntryResponse {
  redirectUrl?: string;
}

export interface UseFlightHubEntryResult {
  /** FlightHub 2 진입 시도 — 성공 시 `redirectUrl` 로 브라우저 전체 이동 */
  enterFlightHub: () => Promise<void>;
  /** 진입 요청 진행 중 여부 (성공 시 페이지 전환까지 true 유지) */
  isEntering: boolean;
}

/**
 * 사이트 머리말 「드론 관제 시스템 접속」 버튼의 진입 로직(flighthub2-sso-access).
 *
 * 진입 API(200)가 준 `redirectUrl` 로 이동시키고, 실패(401/403/502/네트워크
 * 오류/redirectUrl 부재) 시 토스트로 알린 뒤 FlightHub 이동은 발생시키지 않는다.
 * `isEntering` 동안의 재호출은 무시된다(중복 클릭 방지).
 */
export const useFlightHubEntry = (): UseFlightHubEntryResult => {
  const { t } = useTranslation("landing");
  const [isEntering, setIsEntering] = useState(false);
  // 클릭 연타 시 state 반영 전 재진입을 막기 위한 동기 가드
  const isEnteringRef = useRef(false);

  const enterFlightHub = useCallback(async () => {
    if (isEnteringRef.current) return;
    isEnteringRef.current = true;
    setIsEntering(true);

    try {
      const token = await getIdToken();
      const response = await fetch(FLIGHTHUB_ENTRY_ENDPOINT, {
        method: "POST",
        headers: { Authorization: token },
      });
      if (!response.ok) {
        throw new Error(`flighthub entry rejected: ${response.status}`);
      }

      const body = (await response.json()) as FlightHubEntryResponse;
      if (!body.redirectUrl) {
        throw new Error("flighthub entry response missing redirectUrl");
      }

      // 성공 — 페이지 전환이 완료될 때까지 isEntering 을 유지한다 (해제하지 않음)
      window.location.assign(body.redirectUrl);
    } catch {
      notice("error", t("site.account.flightHubFailed"));
      isEnteringRef.current = false;
      setIsEntering(false);
    }
  }, [t]);

  return { enterFlightHub, isEntering };
};
